import React, {FC, useState} from "react";
import {
  Container,
  CssBaseline,
  Avatar,
  Button,
  Link,
  Typography,
  ThemeProvider,
  Box,
  createTheme,
  Snackbar,
  Alert,
  FilledInput,
  IconButton,
  InputAdornment,
  InputLabel,
  FormControl,
  CircularProgress,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import {VisibilityOff, Visibility} from "@mui/icons-material";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useResetPasswordMutation} from "../../app/services/account";

const theme = createTheme();

/**
 * Completes a password reset. Reads the email and token from the query string (as embedded in the
 * emailed link), collects a new password, and submits it to the API.
 */
const ResetPasswordPage: FC = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") ?? "";
  const token = searchParams.get("token") ?? "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [resetPassword, {isLoading}] = useResetPasswordMutation();
  const navigate = useNavigate();

  const isLinkValid = Boolean(email) && Boolean(token);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    await resetPassword({email, token, newPassword, confirmPassword})
      .unwrap()
      .then(() => {
        setIsSuccess(true);
        setSnackbarMessage("Your password has been reset. Please log in.");
        setIsSnackbarOpen(true);
        setTimeout(() => navigate("/login"), 1500);
      })
      .catch(error => {
        setSnackbarMessage(
          error?.data?.Errors?.IdentityError?.[0] ??
            error?.data?.detail ??
            "This password reset link is invalid or has expired. Please request a new one.",
        );
        setIsSnackbarOpen(true);
      });
  }

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="sm">
        <CssBaseline />

        <Snackbar
          open={isSnackbarOpen}
          autoHideDuration={6000}
          onClose={() => setIsSnackbarOpen(false)}
          anchorOrigin={{vertical: "top", horizontal: "center"}}>
          <Alert
            severity={isSuccess ? "success" : "error"}
            onClose={() => setIsSnackbarOpen(false)}>
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <Box
          sx={{
            my: 16,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}>
          <Avatar sx={{m: 1, bgcolor: "secondary.main"}}>
            <LockResetIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Reset password
          </Typography>

          {!isLinkValid ? (
            <Box sx={{mt: 3, textAlign: "center"}}>
              <Alert severity="error" sx={{mb: 2}}>
                This password reset link is invalid or incomplete. Please request a new one.
              </Alert>
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() => navigate("/forgot-password")}>
                <Typography variant="body2" color="primary">
                  Request a new reset link
                </Typography>
              </Link>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} sx={{mt: 1, width: "100%"}}>
              <FormControl sx={{my: 1, width: "100%"}} variant="filled">
                <InputLabel htmlFor="newPassword">New password</InputLabel>
                <FilledInput
                  name="newPassword"
                  type={isPasswordVisible ? "text" : "password"}
                  required
                  autoFocus
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                        onMouseDown={e => e.preventDefault()}
                        edge="end">
                        {isPasswordVisible ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  sx={{input: {background: "white"}}}
                />
              </FormControl>

              <FormControl sx={{my: 1, width: "100%"}} variant="filled">
                <InputLabel htmlFor="confirmPassword">Confirm new password</InputLabel>
                <FilledInput
                  name="confirmPassword"
                  type={isPasswordVisible ? "text" : "password"}
                  required
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  error={confirmPassword.length > 0 && !passwordsMatch}
                  sx={{input: {background: "white"}}}
                />
              </FormControl>

              {confirmPassword.length > 0 && !passwordsMatch && (
                <Typography variant="body2" color="error" sx={{my: 1}}>
                  Passwords do not match.
                </Typography>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{mt: 3, mb: 2}}
                disabled={!passwordsMatch || isLoading}>
                Reset password
                {isLoading && (
                  <CircularProgress
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: -12,
                      marginLeft: -12,
                    }}
                    size={24}
                  />
                )}
              </Button>

              <Box sx={{textAlign: "center"}}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => navigate("/login")}>
                  <Typography variant="body2" color="primary">
                    Back to login
                  </Typography>
                </Link>
              </Box>
            </Box>
          )}
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default ResetPasswordPage;
